import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Power, PowerOff, TimerReset } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errText } from "@/lib/api";
import { fmtDateTime, fmtRelative, tzAbbr, viewerTz } from "@/lib/time";
import { Countdown } from "../Countdown";

const DURATIONS = [[null, "Until I bring it back"], [15, "15 min"], [30, "30 min"], [60, "1 hour"], [120, "2 hours"], [240, "4 hours"], ["custom", "Custom"]];
const chip = (on) => `border px-3.5 py-2 text-sm font-semibold transition-colors ${on ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-black/20 bg-white hover:border-[#0A0A0A]"}`;

const OfflineForm = ({ onDone }) => {
  const [pick, setPick] = useState(null);
  const [amount, setAmount] = useState(90);
  const [unit, setUnit] = useState(1);
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const minutes = pick === "custom" ? Math.round(Math.max(1, Number(amount) || 1) * unit) : pick;

  const go = async () => {
    setBusy(true);
    try { await api.post("/maintenance/offline", { minutes, reason }); toast.success("The site is now offline to visitors"); onDone(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); setConfirm(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 text-sm font-bold">How long?</p>
        <div className="flex flex-wrap gap-2">
          {DURATIONS.map(([v, l]) => <button key={l} type="button" onClick={() => setPick(v)} className={chip(pick === v)} data-testid={`offline-duration-${v ?? "manual"}`}>{l}</button>)}
        </div>
        {pick === "custom" && (
          <div className="mt-3 flex gap-2">
            <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} className="w-28 border border-black/20 bg-white px-3 py-2.5 text-sm" data-testid="offline-custom-amount" />
            <select value={unit} onChange={(e) => setUnit(Number(e.target.value))} className="border border-black/20 bg-white px-3 py-2.5 text-sm" data-testid="offline-custom-unit">
              <option value={1}>minutes</option><option value={60}>hours</option><option value={1440}>days</option>
            </select>
          </div>
        )}
        <p className="mt-2 text-xs text-[#57534E]">{minutes ? `Comes back online by itself ${fmtDateTime(new Date(Date.now() + minutes * 60000).toISOString())} (${tzAbbr(new Date().toISOString())}).` : "Stays offline until you press “Bring site back online”."}</p>
      </div>
      <label className="block"><span className="mb-1.5 block text-sm font-bold">Note for your history log <span className="font-normal text-[#57534E]">(optional, private)</span></span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="e.g. Updating the home page" className="w-full border border-black/20 bg-white px-3 py-2.5 text-sm" data-testid="offline-reason" />
      </label>
      <button type="button" onClick={() => setConfirm(true)} data-testid="offline-take-offline" className="flex items-center gap-2 bg-[#E62314] px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#0A0A0A]">
        <PowerOff className="h-4 w-4" />Take site offline now
      </button>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="offline-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Take the site offline?</AlertDialogTitle>
            <AlertDialogDescription>Visitors will see the maintenance page straight away. You'll still see and edit the full site while logged in.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="offline-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); go(); }} disabled={busy} className="bg-[#E62314] hover:bg-[#0A0A0A]" data-testid="offline-confirm-yes">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Take offline
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const OfflineActions = ({ status, onDone }) => {
  const [busy, setBusy] = useState("");
  const run = async (k, fn, msg) => { setBusy(k); try { await fn(); toast.success(msg); onDone(); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); } };
  const scheduled = status.mode === "scheduled";
  return (
    <div className="flex flex-wrap gap-2">
      {scheduled ? (
        <button type="button" disabled={!!busy} onClick={() => run("end", () => api.post(`/maintenance/windows/${status.window_id}/skip`, { start: status.started_at }), "Maintenance ended. The site is live")}
          data-testid="offline-end-scheduled" className="flex items-center gap-2 bg-[#0A0A0A] px-6 py-3.5 text-sm font-bold text-white hover:bg-[#16A34A]">
          {busy === "end" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}End this maintenance now
        </button>
      ) : (
        <>
          <button type="button" disabled={!!busy} onClick={() => run("on", () => api.post("/maintenance/online"), "The site is back online")}
            data-testid="offline-bring-online" className="flex items-center gap-2 bg-[#16A34A] px-6 py-3.5 text-sm font-bold text-white hover:bg-[#0A0A0A]">
            {busy === "on" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}Bring site back online
          </button>
          {[15, 30, 60].map((m) => (
            <button key={m} type="button" disabled={!!busy} onClick={() => run(`x${m}`, () => (status.ends_at ? api.post("/maintenance/extend", { minutes: m }) : api.post("/maintenance/offline", { minutes: m })), status.ends_at ? `Extended by ${m} min` : `Comes back in ${m} min`)}
              data-testid={`offline-extend-${m}`} className="flex items-center gap-2 border border-black/20 bg-white px-4 py-3.5 text-sm font-semibold hover:border-[#0A0A0A]">
              <TimerReset className="h-4 w-4" />{status.ends_at ? `+${m < 60 ? `${m} min` : "1 hour"}` : `Back in ${m < 60 ? `${m} min` : "1 hour"}`}
            </button>
          ))}
        </>
      )}
    </div>
  );
};

export const LiveControl = ({ status, onDone }) => {
  const off = !!status?.active;
  const tz = viewerTz();
  return (
    <section data-testid="maintenance-live-control" className={`border p-6 sm:p-8 ${off ? "border-[#E62314] bg-[#E62314]/[0.04]" : "border-black/15 bg-white"}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="flex items-center gap-2.5 font-display text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="maintenance-site-state">
            <span className={`h-3 w-3 rounded-full ${off ? "bg-[#E62314]" : "bg-[#16A34A]"}`} />{off ? "Offline to visitors" : "Live"}
          </p>
          <p className="mt-2 max-w-xl text-sm text-[#57534E]" data-testid="maintenance-state-detail">
            {!off && "Everyone can see your website right now."}
            {off && status.mode === "manual" && <>Taken offline {status.started_at ? fmtRelative(status.started_at) : ""}. {status.ends_at ? <>Comes back automatically <strong className="text-[#0A0A0A]">{fmtDateTime(status.ends_at, tz)}</strong>.</> : "Stays offline until you bring it back."}</>}
            {off && status.mode === "scheduled" && <>Scheduled maintenance “{status.title}” is running. Ends <strong className="text-[#0A0A0A]">{fmtDateTime(status.ends_at, tz)}</strong>.</>}
          </p>
        </div>
        {off && status.ends_at && <div className="font-display text-2xl font-extrabold text-[#E62314]"><Countdown target={status.ends_at} compact onDone={onDone} testId="maintenance-admin-countdown" /></div>}
      </div>
      <div className="mt-7">{off ? <OfflineActions status={status} onDone={onDone} /> : <OfflineForm onDone={onDone} />}</div>
    </section>
  );
};
